// Kept identical across the independently deployed game frontends.
import React from 'react';
import { hubHeaders } from '@precisionpros/game-ui';
import './LeaderboardStats.css';

export function formatRecordTime(milliseconds) {
  const ms = Math.round(milliseconds);
  return `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`;
}
const shortDate = value => {
  const [year, month, day] = value.split('-');
  return `${Number(month)}/${Number(day)}/${year.slice(-2)}`;
};
const medal = rank => ['🥇', '🥈', '🥉'][rank - 1] || rank;

function StatList({ title, rows = [], value, empty = 'No records yet.' }) {
  return <div className="lb-stat-group"><h3>{title}</h3>
    {rows.length ? <ol className="lb-stat-list">{rows.map((row, index) => <li key={`${row.player_name}-${index}`}>
      <span className="lb-stat-place" aria-label={`Rank ${row.rank}`}>{medal(row.rank)}</span>
      <span className="lb-stat-player">{row.player_name}</span><strong>{value(row)}</strong>
      {row.date && <small>{shortDate(row.date)}</small>}
      {row.start_date && <small>{shortDate(row.start_date)}–{shortDate(row.end_date)}</small>}
    </li>)}</ol> : <p className="lb-stat-empty">{empty}</p>}
  </div>;
}

export default function LeaderboardStats({ endpoint, date, pointsFriday = false, headers }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [retry, setRetry] = React.useState(0);
  React.useEffect(() => {
    const controller = new AbortController();
    setData(null); setError('');
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    fetch(`${endpoint}${query}`, { headers: { ...hubHeaders(), ...headers }, cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('We couldn’t load the leaderboard stats.');
        const result = await response.json();
        if (!result.weekly || !result.all_time) throw new Error('We couldn’t load the leaderboard stats.');
        if (!controller.signal.aborted) setData(result);
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.message); });
    return () => controller.abort();
  }, [endpoint, date, retry, headers]);
  if (error) return <section className="lb-stats-card" role="alert">{error} <button type="button" onClick={() => setRetry(value => value + 1)}>Try again</button></section>;
  if (!data) return <section className="lb-stats-card" role="status">Loading leaderboard stats…</section>;
  const { weekly, all_time: allTime, perfect_only: perfectOnly } = data;
  const speedTitle = perfectOnly ? 'Fastest perfect solves' : 'Fastest solves';
  const time = row => formatRecordTime(row.time_ms);
  const wins = row => `${row.wins} ${row.wins === 1 ? 'win' : 'wins'}`;
  return <>
    <section className="lb-stats-card" aria-label="Weekly Stats">
      <h2>Weekly Stats</h2><p className="lb-stat-period">{shortDate(weekly.week_start)}–{shortDate(weekly.week_end)}</p>
      <div className="lb-stats-grid">
        <StatList title="First-place finishes" rows={weekly.first_place} value={wins} />
        <StatList title={speedTitle} rows={weekly.fastest} value={time} />
        <div><StatList title="Best average time" rows={weekly.average_time} value={time} empty="No qualifiers yet." />
          <p className="lb-stat-note">Requires a completed timed game every {weekly.timed_days_only ? 'timed-game day' : 'day'} so far that week ({weekly.required_days} {weekly.timed_days_only ? 'timed days' : 'of 7 days'} counted).{weekly.timed_days_only && ' Points Friday is excluded.'}</p>
        </div>
      </div>
      {perfectOnly && <p className="lb-stat-note">Fastest solves require a perfect result. Weekly averages include all completed timed solves.</p>}
    </section>
    <section className="lb-stats-card" aria-label="All-Time Stats">
      <h2>All-Time Stats</h2><div className="lb-stats-grid">
        <StatList title="First-place finishes" rows={allTime.first_place} value={wins} />
        <StatList title={speedTitle} rows={allTime.fastest} value={time} />
        <StatList title="Longest winning streak" rows={allTime.longest_streaks} value={row => `${row.streak} ${row.streak === 1 ? 'day' : 'days'}`} />
        {pointsFriday && <StatList title="Highest Points Friday scores" rows={allTime.highest_scores} value={row => `${row.points} pts`} />}
      </div>
      <p className="lb-stat-note">Winning streaks count consecutive days finishing first{perfectOnly ? ' with a perfect solve' : ''}. Tied first places count as wins.</p>
    </section>
  </>;
}
